import { useState, useCallback } from 'react';
import { Building2, MapPin, UsersRound, Loader2, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@flote/ui';
import { deliveryTsr } from '../../api';
import { HubFormModal } from '../../components/hubs/HubFormModal';
import { getErrorMessage } from '../../lib/error-utils';
import type { CreateHubRequest, Hub, UpdateHubRequest } from '@flote/contracts/http';

function StatCard({
  title,
  count,
  icon: Icon,
  color,
  isLoading,
}: {
  title: string;
  count: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className={`rounded-full p-2 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          {isLoading ? (
            <div className="h-8 w-8 animate-pulse rounded bg-muted" />
          ) : (
            <p className="text-2xl font-bold">{count}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function formatAddress(hub: Hub): string {
  const parts = [];
  if (hub.address.street) parts.push(hub.address.street);
  if (hub.address.city) parts.push(hub.address.city);
  if (hub.address.state) parts.push(hub.address.state);
  if (hub.address.postalCode) parts.push(hub.address.postalCode);
  return parts.join(', ') || 'No address';
}

export function HubsPage() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedHub, setSelectedHub] = useState<Hub | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Fetch hubs list
  const { data: hubsResponse, isLoading: hubsLoading, refetch: refetchHubs } = deliveryTsr.adminHubs.list.useQuery({
    queryKey: ['hubs-list'],
  });
  const hubs = hubsResponse?.status === 200 ? hubsResponse.body : [];

  // Fetch teams for reference
  const { data: teamsResponse } = deliveryTsr.adminTeams.list.useQuery({
    queryKey: ['teams-list'],
  });
  const teams = teamsResponse?.status === 200 ? teamsResponse.body : [];

  // Mutations
  const createHubMutation = deliveryTsr.adminHubs.create.useMutation();
  const updateHubMutation = deliveryTsr.adminHubs.update.useMutation();
  const syncHubsMutation = deliveryTsr.adminHubs.sync.useMutation();

  // Compute stats
  const totalHubs = hubs.length;
  const hubsWithTeams = hubs.filter(h => h.teams.length > 0).length;
  const totalTeamsWithHubs = teams.filter(t => t.hub !== null).length;

  const handleCreateHub = useCallback(async (data: CreateHubRequest) => {
    setIsProcessing(true);
    try {
      const response = await createHubMutation.mutateAsync({
        body: data,
      });

      if (response.status === 201) {
        toast.success('Hub created successfully');
        setIsCreateModalOpen(false);
        refetchHubs();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to create hub');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create hub'));
      console.error('Create hub error:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [createHubMutation, refetchHubs]);

  const handleUpdateHub = useCallback(async (data: UpdateHubRequest) => {
    if (!selectedHub) return;

    setIsProcessing(true);
    try {
      const response = await updateHubMutation.mutateAsync({
        params: { hubId: selectedHub.id },
        body: data,
      });

      if (response.status === 200) {
        toast.success('Hub updated successfully');
        setIsEditModalOpen(false);
        setSelectedHub(null);
        refetchHubs();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to update hub');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update hub'));
      console.error('Update hub error:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [selectedHub, updateHubMutation, refetchHubs]);

  const handleRowClick = (hub: Hub) => {
    setSelectedHub(hub);
    setIsEditModalOpen(true);
  };

  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      const response = await syncHubsMutation.mutateAsync({
        body: {},
      });

      if (response.status === 200) {
        toast.success(`Synced ${response.body.synced} hubs from Onfleet`);
        refetchHubs();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to sync hubs');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to sync hubs'));
      console.error('Sync hubs error:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [syncHubsMutation, refetchHubs]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Hubs</h1>
          <p className="text-muted-foreground">
            Manage delivery hubs and distribution centers
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={isSyncing}
            className="w-full sm:w-auto"
          >
            {isSyncing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Sync from Onfleet
          </Button>
          <Button onClick={() => setIsCreateModalOpen(true)} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            Create Hub
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Total Hubs"
          count={totalHubs}
          icon={Building2}
          color="bg-blue-100 text-blue-600"
          isLoading={hubsLoading}
        />
        <StatCard
          title="Hubs with Teams"
          count={hubsWithTeams}
          icon={UsersRound}
          color="bg-green-100 text-green-600"
          isLoading={hubsLoading}
        />
        <StatCard
          title="Teams Assigned"
          count={totalTeamsWithHubs}
          icon={MapPin}
          color="bg-purple-100 text-purple-600"
          isLoading={hubsLoading}
        />
      </div>

      {/* Hubs List */}
      <Card>
        <CardHeader>
          <CardTitle>Hub Locations</CardTitle>
        </CardHeader>
        <CardContent>
          {hubsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : hubs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Building2 className="mb-4 h-12 w-12 text-muted-foreground/50" />
              <h3 className="mb-2 text-lg font-medium">No hubs found</h3>
              <p className="max-w-sm text-sm text-muted-foreground">
                Create your first hub to set up a home base for your delivery teams.
                Hubs are required for route optimization.
              </p>
              <Button className="mt-4" onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Hub
              </Button>
            </div>
          ) : (
            <div className="-mx-4 overflow-x-auto sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead className="hidden sm:table-cell">Address</TableHead>
                      <TableHead className="hidden md:table-cell">Location</TableHead>
                      <TableHead>Teams</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hubs.map((hub) => (
                      <TableRow
                        key={hub.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(hub)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="hidden h-8 w-8 items-center justify-center rounded-full bg-blue-100 sm:flex">
                              <Building2 className="h-4 w-4 text-blue-600" />
                            </div>
                            <span className="font-medium">{hub.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">{formatAddress(hub)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden text-muted-foreground font-mono text-sm md:table-cell">
                          {hub.location.lat.toFixed(4)}, {hub.location.lng.toFixed(4)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={hub.teams.length > 0 ? 'default' : 'secondary'}>
                            {hub.teams.length} teams
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Hub Modal */}
      <HubFormModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSubmit={handleCreateHub}
        isProcessing={isProcessing}
        teams={teams}
      />

      {/* Edit Hub Modal */}
      <HubFormModal
        open={isEditModalOpen}
        onOpenChange={(open) => {
          setIsEditModalOpen(open);
          if (!open) setSelectedHub(null);
        }}
        onSubmit={handleUpdateHub}
        isProcessing={isProcessing}
        teams={teams}
        hub={selectedHub || undefined}
      />
    </div>
  );
}
